// ─────────────────────────────────────────────────────────────────────────────
// disco-neon.tsx — b08 "Disko" 5:7 card layout
//
// Theme: a 21st birthday at a neon-disco / 90s rave club. Pitch-black with a
// purple tint, electric magenta + cyan + chartreuse + ultraviolet accents.
// Bodoni Moda italic, neon-tube text-shadow stack, mirror ball with diagonal
// light beams, criss-crossing laser lines, equalizer bars at the bottom edge,
// vinyl record bottom-left and a chrome-ringed circular photo. Maximalist
// dark-mode club energy at thumbnail size — every layer reads from across the
// gallery grid.
// ─────────────────────────────────────────────────────────────────────────────
import { Photo, type LayoutCtx } from "./_shared";

const CYAN = "#00E0FF";
const CHARTREUSE = "#DBFF00";
const ULTRAVIOLET = "#B23BFF";

export function DiscoNeonLayout({ names, date, venue, accent, ink, bg, font, photo, photos }: LayoutCtx) {
  const heroPhoto = photo || photos?.[0];
  // Neon-tube glow stack — outer halo + inner core.
  const glowMagenta = `0 0 2px ${ink}, 0 0 6px ${accent}, 0 0 14px ${accent}, 0 0 26px ${accent}cc`;
  const glowCyan = `0 0 2px ${ink}, 0 0 6px ${CYAN}, 0 0 14px ${CYAN}, 0 0 26px ${CYAN}cc`;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(120% 80% at 50% 0%, ${ULTRAVIOLET}3a 0%, ${bg} 55%, #000 100%)`,
        backgroundColor: bg,
        color: ink,
        overflow: "hidden",
        fontFamily: font,
      }}
    >
      {/* MIRROR BALL — top center, silver-cyan reflective with diagonal beams */}
      <svg
        viewBox="0 0 200 200"
        aria-hidden
        style={{
          position: "absolute",
          top: "-6%",
          left: "50%",
          width: "44%",
          transform: "translateX(-50%)",
          opacity: 0.95,
        }}
      >
        <defs>
          <radialGradient id="mb-disco-card" cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="35%" stopColor={CYAN} />
            <stop offset="80%" stopColor={ULTRAVIOLET} />
            <stop offset="100%" stopColor="#000" />
          </radialGradient>
          <pattern id="mb-disco-card-facets" width="14" height="14" patternUnits="userSpaceOnUse">
            <rect width="14" height="14" fill="url(#mb-disco-card)" />
            <path d="M0 0 L14 14 M14 0 L0 14" stroke="#000" strokeOpacity="0.35" strokeWidth="0.6" />
          </pattern>
        </defs>
        {/* hanger */}
        <line x1="100" y1="0" x2="100" y2="36" stroke={`${ink}55`} strokeWidth="1.2" />
        <circle cx="100" cy="100" r="64" fill="url(#mb-disco-card-facets)" />
        <circle cx="100" cy="100" r="64" fill="none" stroke={CYAN} strokeOpacity="0.6" strokeWidth="1" />
        {/* highlight */}
        <ellipse cx="78" cy="78" rx="14" ry="9" fill="#FFFFFF" opacity="0.8" />
      </svg>

      {/* DIAGONAL LIGHT BEAMS shooting out from the mirror ball */}
      <svg
        viewBox="0 0 100 140"
        preserveAspectRatio="none"
        aria-hidden
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", mixBlendMode: "screen", opacity: 0.55 }}
      >
        <defs>
          <linearGradient id="beam-disco-card-a" x1="50%" y1="20%" x2="0%" y2="120%">
            <stop offset="0%" stopColor={CYAN} stopOpacity="0.9" />
            <stop offset="100%" stopColor={CYAN} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="beam-disco-card-b" x1="50%" y1="20%" x2="100%" y2="120%">
            <stop offset="0%" stopColor={accent} stopOpacity="0.9" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="beam-disco-card-c" x1="50%" y1="20%" x2="50%" y2="120%">
            <stop offset="0%" stopColor={CHARTREUSE} stopOpacity="0.7" />
            <stop offset="100%" stopColor={CHARTREUSE} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points="50,20 -10,140 10,140" fill="url(#beam-disco-card-a)" />
        <polygon points="50,20 110,140 90,140" fill="url(#beam-disco-card-b)" />
        <polygon points="50,20 35,140 65,140" fill="url(#beam-disco-card-c)" />
        <polygon points="50,20 -30,90 -30,110" fill="url(#beam-disco-card-a)" opacity="0.6" />
        <polygon points="50,20 130,90 130,110" fill="url(#beam-disco-card-b)" opacity="0.6" />
      </svg>

      {/* LASER GRID — magenta + cyan + chartreuse criss-cross */}
      <svg
        viewBox="0 0 100 140"
        preserveAspectRatio="none"
        aria-hidden
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", mixBlendMode: "screen", opacity: 0.7 }}
      >
        <line x1="-5" y1="60" x2="105" y2="120" stroke={accent} strokeWidth="0.4" />
        <line x1="-5" y1="120" x2="105" y2="60" stroke={CYAN} strokeWidth="0.4" />
        <line x1="-5" y1="40" x2="105" y2="100" stroke={CHARTREUSE} strokeWidth="0.3" opacity="0.7" />
        <line x1="-5" y1="100" x2="105" y2="40" stroke={accent} strokeWidth="0.3" opacity="0.7" />
        <line x1="-5" y1="80" x2="105" y2="80" stroke={CYAN} strokeWidth="0.25" opacity="0.5" />
      </svg>

      {/* VINYL RECORD — bottom-left */}
      <svg
        viewBox="0 0 100 100"
        aria-hidden
        style={{ position: "absolute", left: "-8%", bottom: "8%", width: "44%", opacity: 0.95 }}
      >
        <defs>
          <radialGradient id="vinyl-disco-card" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1a0f24" />
            <stop offset="100%" stopColor="#000" />
          </radialGradient>
        </defs>
        <circle cx="50" cy="50" r="48" fill="url(#vinyl-disco-card)" />
        {[44, 40, 36, 32, 28, 24, 20].map((r) => (
          <circle key={r} cx="50" cy="50" r={r} fill="none" stroke={`${ink}33`} strokeWidth="0.25" />
        ))}
        <circle cx="50" cy="50" r="14" fill={accent} />
        <circle cx="50" cy="50" r="14" fill="none" stroke={CYAN} strokeWidth="0.6" />
        <text x="50" y="48" textAnchor="middle" fontSize="3.6" fill={ink} fontFamily="var(--font-ui)" fontWeight="700" letterSpacing="0.2em">
          NEON
        </text>
        <text x="50" y="55" textAnchor="middle" fontSize="2.6" fill={ink} fontFamily="var(--font-ui)" fontWeight="600" letterSpacing="0.2em" opacity="0.8">
          21 · TASHKENT
        </text>
        <circle cx="50" cy="50" r="1.4" fill={ink} />
      </svg>

      {/* EQUALIZER BARS — bottom edge, varying heights, neon palette */}
      <svg
        viewBox="0 0 100 14"
        preserveAspectRatio="none"
        aria-hidden
        style={{ position: "absolute", left: 0, right: 0, bottom: 0, width: "100%", height: "12%" }}
      >
        {[
          { x: 4, h: 8, c: accent },
          { x: 18, h: 12, c: CYAN },
          { x: 32, h: 6, c: CHARTREUSE },
          { x: 46, h: 13, c: accent },
          { x: 60, h: 9, c: ULTRAVIOLET },
          { x: 74, h: 11, c: CYAN },
          { x: 88, h: 7, c: CHARTREUSE },
        ].map((b) => (
          <rect
            key={b.x}
            x={b.x}
            y={14 - b.h}
            width="8"
            height={b.h}
            fill={b.c}
            opacity="0.9"
          />
        ))}
      </svg>

      {/* CHROME-FRAMED CIRCULAR PHOTO */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "32%",
          transform: "translateX(-50%)",
          width: "38%",
          aspectRatio: "1 / 1",
          borderRadius: "50%",
          padding: 4,
          background:
            `conic-gradient(from 220deg, #ffffff 0deg, ${CYAN} 60deg, ${ULTRAVIOLET} 140deg, ${accent} 220deg, ${CHARTREUSE} 300deg, #ffffff 360deg)`,
          boxShadow: `0 0 18px ${accent}99, 0 0 36px ${CYAN}55`,
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            overflow: "hidden",
            border: `1px solid ${ink}`,
            background: "#000",
          }}
        >
          <Photo
            src={heroPhoto}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            fallback={
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `radial-gradient(circle, ${ULTRAVIOLET}55, ${bg})`,
                }}
              />
            }
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(circle at 30% 25%, ${CYAN}33, transparent 60%)`,
              mixBlendMode: "screen",
            }}
          />
        </div>
      </div>

      {/* "Jasur" — giant Bodoni italic with magenta neon-tube glow */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "60%",
          textAlign: "center",
          fontFamily: font,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: "clamp(28px, 14vw, 96px)",
          lineHeight: 0.9,
          color: ink,
          textShadow: glowMagenta,
          letterSpacing: "-0.01em",
          padding: "0 6%",
        }}
      >
        {names.split(" ")[0]}
      </div>

      {/* "21" numeral with cyan neon-tube glow */}
      <div
        style={{
          position: "absolute",
          right: "6%",
          top: "8%",
          fontFamily: font,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: "clamp(36px, 16vw, 120px)",
          lineHeight: 1,
          color: ink,
          textShadow: glowCyan,
          mixBlendMode: "screen",
        }}
      >
        21
      </div>

      {/* BOTTOM BANNER — date · venue tracked caps + glow */}
      <div
        style={{
          position: "absolute",
          left: "8%",
          right: "8%",
          bottom: "16%",
          textAlign: "center",
          fontFamily: "var(--font-ui)",
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: "0.32em",
          color: ink,
          textShadow: `0 0 4px ${CHARTREUSE}, 0 0 10px ${CHARTREUSE}aa`,
          padding: "8px 0",
          borderTop: `1px solid ${CHARTREUSE}88`,
          borderBottom: `1px solid ${CHARTREUSE}88`,
          background: `${bg}aa`,
          backdropFilter: "blur(2px)",
        }}
      >
        {venue.split(",")[0].toUpperCase()} · {date}
      </div>
    </div>
  );
}
